import React, { Component } from 'react';
import { Button, Form } from 'reactstrap';
import { inject, observer } from 'mobx-react';
import { defineMessages, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import globalTranslations from '../../../i18n/globalTranslations';
import FormGroupField from '../../form/FormGroupField';
import { modelOf } from '../../../prop-types';
import { createFormField } from '../../../models/FormField';
import CheckboxField from '../../form/CheckboxField';
import FormValidator from '../../../services/FormValidator';
import { createForm } from '../../../models/Form';
import BalanceUser from '../../../models/BalanceUser';

const labels = {
  firstName: <FormattedMessage {...globalTranslations.firstNameTitle} />,
  lastName: <FormattedMessage {...globalTranslations.lastNameTitle} />,
  email: <FormattedMessage {...globalTranslations.emailTitle} />,
  loginName: <FormattedMessage {...globalTranslations.emailOrUsernameTitle} />,
  password: <FormattedMessage {...globalTranslations.passwordTitle} />,
  isActive: <FormattedMessage {...globalTranslations.isActiveLabel} />
};

const messages = defineMessages({
  updatePassword: {
    id: 'myAccount.balanceUserAccountUpdatePassword',
    defaultMessage:
      'Optional. Only change, if you want to change your password.'
  }
});

@observer
export class BalanceUserForm extends Component {
  form = null;
  formName = 'BalanceUserForm';

  constructor(props) {
    super(props);
    this.createForm();
  }

  createForm = () => {
    const { formValidator, user } = this.props;
    this.form = createForm({
      firstName: createFormField(
        { value: user && user.firstname },
        formValidator.firstName(labels.firstName)
      ),
      lastName: createFormField(
        { value: user && user.lastname },
        formValidator.lastName(labels.lastName)
      ),
      email: createFormField(
        { value: user && user.email },
        formValidator.email(labels.email)
      ),
      loginName: createFormField(
        { value: user && user.login_name },
        formValidator.username(labels.loginName)
      ),
      password: createFormField(
        { value: user && user.password },
        user ? null : formValidator.password(labels.password)
      ),
      isActive: createFormField({
        value: user && user.is_active === false ? '' : '1'
      })
    });
  };

  getFirstName = () =>
    this.field('firstName', {
      autoComplete: 'given-name',
      required: true
    });

  getLastName = () =>
    this.field('lastName', {
      autoComplete: 'family-name',
      required: true
    });

  getEmail = () =>
    this.field('email', {
      autoComplete: 'email',
      required: true
    });

  getLoginName = () =>
    this.field('loginName', {
      autocapitalization: 'off',
      autoComplete: 'username',
      required: true
    });

  getPassword = (user) =>
    this.field(
      'password',
      {
        type: 'password',
        autoComplete: 'new-password'
      },
      !!user && <FormattedMessage {...messages.updatePassword} />
    );

  getActiveStatus = (user) =>
    this.checkbox('isActive', {
      options: this.createOption('1', labels.isActive),
      disabled: !!!user
    });

  createOption = (value, label) => ({ value, label });

  renderSubmitButton = (loading, buttonText) => (
    <Button color="primary" disabled={this.form.valid === false || loading}>
      {buttonText}
    </Button>
  );

  handleSubmit = (e) => {
    const { onSubmit } = this.props;

    e.preventDefault();
    this.form.validate();
    if (this.form.valid) {
      onSubmit(this.form.getFieldValues());
    }
  };

  field = (fieldName, props, description) => {
    const field = this.form.fields.get(fieldName);
    return (
      field.active && (
        <FormGroupField
          field={field}
          formName={this.formName}
          fieldName={fieldName}
          label={labels[fieldName]}
          description={description}
          {...props}
        />
      )
    );
  };

  checkbox = (fieldName, props) => {
    const field = this.form.fields.get(fieldName);
    return (
      field.active && (
        <CheckboxField
          field={field}
          formName={this.formName}
          fieldName={fieldName}
          {...props}
        />
      )
    );
  };

  render() {
    const { loading, buttonText, user } = this.props;
    return (
      <Form className={this.formName} noValidate onSubmit={this.handleSubmit}>
        {this.getFirstName()}
        {this.getLastName()}
        {this.getEmail()}
        {this.getLoginName()}
        {this.getPassword(user)}
        {this.getActiveStatus(user)}
        {this.renderSubmitButton(loading, buttonText)}
      </Form>
    );
  }
}

BalanceUserForm.propTypes = {
  loading: PropTypes.bool,
  buttonText: PropTypes.node.isRequired,
  user: modelOf(BalanceUser),
  onSubmit: PropTypes.func.isRequired,
  formValidator: PropTypes.instanceOf(FormValidator).isRequired
};

export default inject('formValidator')(BalanceUserForm);
